import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Input, Button, Card, message, Typography } from 'antd';
import { UserOutlined, LockOutlined, AndroidOutlined, DownloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import QRCode from 'qrcode';

const { Title } = Typography;

const LoginPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    const apkUrl = `${window.location.origin}/app-release.apk`;
    QRCode.toDataURL(apkUrl, {
      width: 150,
      margin: 2,
      color: {
        dark: '#000000',
        light: '#ffffff',
      },
    })
      .then((url) => setQrCodeUrl(url))
      .catch((err) => console.error('QR Code generation error:', err));
  }, []);

  const onFinish = async (values: { email: string; password: string }) => {
    setLoading(true);
    try {
      await login(values.email, values.password);
      message.success(t('auth.loginSuccess'));
      navigate('/');
    } catch (error: any) {
      const errorMessage = error.response?.data?.error ||
                            error.response?.data?.message ||
                            t('auth.invalidCredentials');
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadApk = () => {
    const apkUrl = `${window.location.origin}/app-release.apk`;
    window.open(apkUrl, '_blank');
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#f0f2f5' }}>
      <Card style={{ width: 400 }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <img src="/logo.png" alt="Logo" style={{ height: 120, marginBottom: 1 }} />
          <Title level={2} style={{ marginBottom: 4 }}>{t('auth.loginTitle') || 'Valet Admin'}</Title>
          <span style={{ color: '#888' }}>{t('auth.loginSubtitle')}</span>
        </div>
        <Form name="login" onFinish={onFinish} size="large">
          <Form.Item name="email" rules={[{ required: true, type: 'email', message: t('auth.emailRequired') }]}>
            <Input prefix={<UserOutlined />} placeholder={t('auth.email') || 'Email'} />
          </Form.Item>
          <Form.Item name="password" rules={[{ required: true, message: t('auth.passwordRequired') }]}>
            <Input.Password prefix={<LockOutlined />} placeholder={t('auth.password')} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              {t('auth.loginButton')}
            </Button>
          </Form.Item>
        </Form>

        <div style={{ marginTop: 24, paddingTop: 24, borderTop: '1px solid #f0f0f0', textAlign: 'center' }}>
          <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <AndroidOutlined style={{ fontSize: 24, color: '#52c41a' }} />
            <span style={{ fontSize: 14, color: '#666' }}>{t('auth.downloadMobile')}</span>
            <img src="/favicon.png" alt="Logo" style={{ height: 32 }} />
          </div>
          
          {qrCodeUrl && (
            <div style={{ marginBottom: 12 }}>
              <img 
                src={qrCodeUrl} 
                alt="QR Code" 
                style={{ border: '1px solid #d9d9d9', borderRadius: 4, padding: 8 }}
              />
            </div>
          )}
          
          <Button 
            type="default" 
            icon={<DownloadOutlined />} 
            onClick={handleDownloadApk}
            block
          >
            {t('common.download')}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default LoginPage;
